import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Navbar from "../components/Navbar";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Send, Users, Search, ArrowLeft, Paperclip, X, FileText, Image as ImageIcon, Download, Smile } from "lucide-react";
import Avatar from "../components/Avatar";
import EmojiPicker from "emoji-picker-react";
import AvatarLightbox from "../components/AvatarLightbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WS_URL = process.env.REACT_APP_BACKEND_URL.replace(/^http/, "ws") + "/api/ws/chat";
const MEDIA = process.env.REACT_APP_BACKEND_URL;
const POLL_FALLBACK_INTERVAL = 15000;

const initials = (name) => (name || "?").split(" ").filter(Boolean).slice(0, 2).map(p => p[0]).join("").toUpperCase();
const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1024*1024 ? `${(b/1024).toFixed(0)} KB` : `${(b/1024/1024).toFixed(1)} MB`;

/* ===== Attachment bubble inside a message ===== */
function Attachment({ msg, isMe }) {
  if (!msg.attachment_url) return null;
  const url = `${MEDIA}${msg.attachment_url}`;
  if (msg.attachment_type === "image") {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mt-1 -mx-2 first:mt-0" data-testid="msg-attachment-image">
        <img src={url} alt={msg.attachment_name || "Bild"} className="max-w-full max-h-80 object-contain border border-white/5" />
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" download={msg.attachment_name}
       data-testid="msg-attachment-doc"
       className={`mt-1 flex items-center gap-2.5 px-3 py-2 border transition-colors ${
         isMe ? "bg-black/10 border-black/20 hover:bg-black/20 text-black" : "bg-white/[0.04] border-white/10 hover:bg-white/[0.07] text-white"
       }`}>
      <FileText size={18} className={isMe ? "text-black/60" : "text-[#00e5ff]"} />
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-outfit font-semibold truncate">{msg.attachment_name || "Datei"}</p>
        <p className={`text-[11px] ${isMe ? "text-black/50" : "text-white/45"}`}>{fmtSize(msg.attachment_size || 0)}</p>
      </div>
      <Download size={14} className={isMe ? "text-black/50" : "text-white/45"} />
    </a>
  );
}

/* ===== MessageList (WhatsApp bubble style) ===== */
function MessageList({ messages, me, messagesEndRef, emptyText, onAvatarClick }) {
  let lastDate = "";
  const formatTime = (iso) => new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const formatDateHeader = (iso) => {
    const d = new Date(iso); const today = new Date(); const y = new Date(Date.now() - 86400000);
    if (d.toDateString() === today.toDateString()) return "Heute";
    if (d.toDateString() === y.toDateString()) return "Gestern";
    return d.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long" });
  };
  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-1" data-testid="chat-messages"
         style={{ background: "radial-gradient(circle at 20% 10%, rgba(0,229,255,0.03) 0%, transparent 55%), radial-gradient(circle at 90% 90%, rgba(168,85,247,0.025) 0%, transparent 55%), #050505" }}>
      {messages.length === 0 && <p className="text-center text-white/35 text-sm py-10 font-outfit">{emptyText || "Noch keine Nachrichten."}</p>}
      {messages.map((msg) => {
        const dateStr = new Date(msg.created_at).toDateString();
        let showDate = false;
        if (dateStr !== lastDate) { showDate = true; lastDate = dateStr; }
        const myId = me?._id || me?.id;
        const senderId = msg.author_id || msg.sender_id;
        const isMe = !!myId && !!senderId && String(myId) === String(senderId);
        const authorName = msg.author_name || msg.sender_name || "";
        const authorAvatar = msg.author_avatar_url || msg.sender_avatar_url || "";
        const authorId = msg.author_id || msg.sender_id || "";
        const hasText = !!msg.content;
        return (
          <div key={msg.id}>
            {showDate && (
              <div className="flex justify-center my-4">
                <span className="text-[10px] uppercase tracking-[0.28em] text-white/50 bg-white/[0.05] border border-white/[0.06] px-3 py-1 font-outfit font-medium">{formatDateHeader(msg.created_at)}</span>
              </div>
            )}
            <div className={`flex items-end gap-2 ${isMe ? "justify-end" : "justify-start"} mb-1.5`} data-testid={`msg-${msg.id}`}>
              {!isMe && (
                <button
                  type="button"
                  onClick={() => onAvatarClick?.({
                    avatar_url: authorAvatar, name: authorName, subline: "", id: authorId,
                  })}
                  data-testid={`msg-avatar-${msg.id}`}
                  className="shrink-0 mb-1 hover:ring-2 hover:ring-[#00e5ff]/50 rounded-full transition-all"
                  title="Profilbild vergrößern"
                >
                  <Avatar src={authorAvatar} name={authorName || "?"} size={28} />
                </button>
              )}
              <div className={`max-w-[78%] sm:max-w-[70%] px-3.5 py-2 relative ${
                isMe
                  ? "bg-[#00e5ff] text-black rounded-[2px] rounded-br-none shadow-[0_0_24px_-8px_rgba(0,229,255,0.55)]"
                  : "bg-white/[0.06] text-white border border-white/[0.07] rounded-[2px] rounded-bl-none"
              }`}>
                {!isMe && <div className="text-[11px] font-outfit font-semibold text-[#00e5ff]/80 mb-0.5 tracking-tight">{authorName}</div>}
                <Attachment msg={msg} isMe={isMe} />
                {hasText && <p className={`text-[14px] leading-snug whitespace-pre-wrap break-words ${isMe ? "text-black" : "text-white/90"} ${msg.attachment_url ? "mt-1.5" : ""}`}>{msg.content}</p>}
                <div className={`text-[10px] mt-1 text-right font-outfit ${isMe ? "text-black/55" : "text-white/40"}`}>{formatTime(msg.created_at)}</div>
              </div>
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
}

/* ===== Chat Input with file picker + CTRL-V paste ===== */
function ChatInput({ value, onChange, onSend, sending, placeholder, testId, sendTestId, attachment, setAttachment }) {
  const fileRef = useRef(null);
  const [uploadErr, setUploadErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setUploadErr("Datei zu groß (max 20 MB)"); return; }
    setUploading(true); setUploadErr("");
    try {
      const fd = new FormData(); fd.append("file", file, file.name || "paste.png");
      const { data } = await axios.post(`${API}/uploads/chat-file`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setAttachment(data);
    } catch (e) {
      setUploadErr(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally { setUploading(false); }
  }, [setAttachment]);

  const onPaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type?.startsWith("image/")) {
        const f = item.getAsFile();
        if (f) { e.preventDefault(); upload(f); return; }
      }
    }
    // Text paste: let browser handle natively
  }, [upload]);

  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);

  useEffect(() => {
    if (!showEmoji) return;
    const close = (e) => { if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showEmoji]);

  return (
    <div className="px-3 sm:px-5 py-3 border-t border-white/[0.06] bg-black/60 backdrop-blur-sm" data-testid="chat-input-area">
      {(attachment || uploading || uploadErr) && (
        <div className="mb-2 flex items-center gap-2 px-3 py-2 border border-white/10 bg-white/[0.03]">
          {uploading ? (
            <span className="text-[12px] text-white/60 font-outfit">Lade Anhang hoch...</span>
          ) : attachment ? (
            <>
              {attachment.attachment_type === "image" ? (
                <ImageIcon size={14} className="text-[#00e5ff]" />
              ) : (
                <FileText size={14} className="text-[#00e5ff]" />
              )}
              <span className="text-[12px] text-white/80 font-outfit font-semibold truncate flex-1" data-testid="attachment-preview">{attachment.attachment_name}</span>
              <span className="text-[11px] text-white/40">{fmtSize(attachment.attachment_size)}</span>
              <button type="button" onClick={() => setAttachment(null)} data-testid="attachment-remove"
                className="text-white/50 hover:text-red-400 transition-colors"><X size={14} /></button>
            </>
          ) : (
            <span className="text-[12px] text-red-400 font-outfit">{uploadErr}</span>
          )}
        </div>
      )}
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" className="hidden"
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
          onChange={(e) => upload(e.target.files?.[0])} data-testid="chat-file-input" />
        <button onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn"
          className="w-11 h-11 flex items-center justify-center text-white/50 hover:text-[#00e5ff] hover:bg-white/[0.04] rounded-full transition-colors" title="Anhang hinzufügen">
          <Paperclip size={17} />
        </button>
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
          data-testid={testId} onPaste={onPaste}
          className="flex-1 bg-white/[0.04] border-white/10 text-white rounded-full focus:border-[#00e5ff]/60 focus:ring-0 h-11 text-[14px] px-4"
          onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), onSend())} />
        <Button onClick={onSend} disabled={sending || (!value.trim() && !attachment)} data-testid={sendTestId}
          className="bg-[#00e5ff] text-black hover:bg-[#00e5ff] hover:shadow-[0_0_32px_-4px_rgba(0,229,255,0.6)] rounded-full h-11 w-11 p-0 transition-all disabled:opacity-40">
          <Send size={15} />
        </Button>
        <div className="relative" ref={emojiRef}>
          <button onClick={() => setShowEmoji(!showEmoji)} data-testid="chat-emoji-btn"
            className="w-11 h-11 flex items-center justify-center text-white/50 hover:text-[#00e5ff] hover:bg-white/[0.04] rounded-full transition-colors" title="Emoji">
            <Smile size={17} />
          </button>
          {showEmoji && (
            <div className="absolute bottom-14 right-0 z-50" data-testid="emoji-picker">
              <EmojiPicker onEmojiClick={(e) => { onChange(value + e.emoji); setShowEmoji(false); }}
                theme="dark" width={350} height={400} searchPlaceholder="Emoji suchen..."
                skinTonesDisabled previewConfig={{ showPreview: false }}
                lazyLoadEmojis />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===================================================================== */
export default function ChatPage() {
  const { user } = useAuth();
  const me = user;

  const [active, setActive] = useState({ type: "group", channel: "allgemein" });
  const [peerPreview, setPeerPreview] = useState(null);

  const [groupMessages, setGroupMessages] = useState([]);
  const groupLastTime = useRef(null);

  const [conversations, setConversations] = useState([]);
  const [dmMessages, setDmMessages] = useState([]);
  const dmLastTime = useRef(null);

  const [input, setInput] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showSidebarOnMobile, setShowSidebarOnMobile] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const currentSubRef = useRef(null);

  /* ------------ Initial loads ------------ */
  // (channels + contacts endpoints are no longer used — we only show active DM
  // conversations; users start new DMs from the Members page.)

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/dm/conversations`, { withCredentials: true });
      setConversations(data);
    } catch (err) {
      console.debug("[chat] loadConversations failed:", err?.response?.status);
    }
  }, []);
  useEffect(() => { loadConversations(); }, [loadConversations]);

  /* Deep-link ?dm=... */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const dmId = params.get("dm");
    if (!dmId) return;
    const name = decodeURIComponent(params.get("name") || "");
    const company = decodeURIComponent(params.get("company") || "");
    setActive({ type: "dm", peer: { id: dmId, name, company } });
    setShowSidebarOnMobile(false);
    // Always lookup avatar_url from members
    axios.get(`${API}/members`, { withCredentials: true }).then(({ data }) => {
      const m = (data || []).find((x) => x.id === dmId);
      if (m) {
        setActive({
          type: "dm",
          peer: {
            id: dmId,
            name: `${m.first_name || ""} ${m.last_name || ""}`.trim(),
            company: m.company || "",
            avatar_url: m.avatar_url || "",
          },
        });
      }
    }).catch(() => {});
  }, []);

  /* ------------ WebSocket lifecycle ------------ */
  useEffect(() => {
    let reconnectTimer;
    let cancelled = false;

    const connect = () => {
      try {
        const ws = new WebSocket(WS_URL);
        wsRef.current = ws;
        ws.onopen = () => setWsConnected(true);
        ws.onclose = () => {
          setWsConnected(false);
          if (!cancelled) reconnectTimer = setTimeout(connect, 2500);
        };
        ws.onerror = () => { /* handled via close */ };
        ws.onmessage = (ev) => {
          let data; try { data = JSON.parse(ev.data); } catch { return; }
          if (data.type === "group_message") {
            // Append only if it's our active group channel
            setGroupMessages((prev) => {
              // de-dup by id
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            groupLastTime.current = data.message.created_at;
          } else if (data.type === "dm_message") {
            setDmMessages((prev) => {
              if (prev.some((m) => m.id === data.message.id)) return prev;
              return [...prev, data.message];
            });
            dmLastTime.current = data.message.created_at;
            loadConversations();
          }
        };
      } catch (wsErr) {
        console.debug("[ws] connect failed, will retry:", wsErr?.message);
        if (!cancelled) reconnectTimer = setTimeout(connect, 2500);
      }
    };
    connect();
    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      try {
        wsRef.current?.close();
      } catch (closeErr) {
        console.debug("[ws] close on unmount failed:", closeErr?.message);
      }
    };
  }, [loadConversations]);

  /* Re-subscribe on active change */
  useEffect(() => {
    if (!wsConnected) return;
    const room = active.type === "group" ? `group:${active.channel}` : `dm:${[me?._id || me?.id, active.peer?.id].sort().join("_")}`;
    if (currentSubRef.current === room) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (currentSubRef.current) ws.send(JSON.stringify({ type: "unsubscribe", room: currentSubRef.current }));
    ws.send(JSON.stringify({ type: "subscribe", room }));
    currentSubRef.current = room;
  }, [active, wsConnected, me]);

  /* ------------ Initial fetch of history ------------ */
  const fetchGroup = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/chat/messages`, { params: { channel: active.channel }, withCredentials: true });
      setGroupMessages(data);
      groupLastTime.current = data.length ? data[data.length - 1].created_at : null;
      setError("");
    } catch { setError("Nachrichten konnten nicht geladen werden."); }
  }, [active]);

  const fetchDm = useCallback(async () => {
    if (active.type !== "dm") return;
    try {
      const { data } = await axios.get(`${API}/chat/dm/with/${active.peer.id}`, { withCredentials: true });
      setDmMessages(data);
      dmLastTime.current = data.length ? data[data.length - 1].created_at : null;
    } catch { setError("Nachrichten konnten nicht geladen werden."); }
  }, [active]);

  useEffect(() => {
    if (active.type === "group") fetchGroup();
    else fetchDm();
  }, [active, fetchGroup, fetchDm]);

  /* Fallback polling when WS disconnected */
  useEffect(() => {
    if (wsConnected) return;
    const id = setInterval(() => {
      if (active.type === "group") fetchGroup();
      else { fetchDm(); loadConversations(); }
    }, POLL_FALLBACK_INTERVAL);
    return () => clearInterval(id);
  }, [wsConnected, active, fetchGroup, fetchDm, loadConversations]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [groupMessages, dmMessages]);

  /* ------------ Send ------------ */
  const handleSend = useCallback(async () => {
    if (!input.trim() && !attachment) return;
    setSending(true); setError("");
    const body = {
      content: input,
      ...(attachment ? {
        attachment_url: attachment.attachment_url,
        attachment_name: attachment.attachment_name,
        attachment_type: attachment.attachment_type,
        attachment_mime: attachment.attachment_mime,
        attachment_size: attachment.attachment_size,
      } : {}),
    };
    try {
      if (active.type === "group") {
        const { data } = await axios.post(`${API}/chat/messages`, { ...body, channel: active.channel }, { withCredentials: true });
        // Optimistic add (WS echo will be deduped by id)
        setGroupMessages((p) => p.some((m) => m.id === data.id) ? p : [...p, data]);
        groupLastTime.current = data.created_at;
      } else {
        const { data } = await axios.post(`${API}/chat/dm/with/${active.peer.id}`, body, { withCredentials: true });
        setDmMessages((p) => p.some((m) => m.id === data.id) ? p : [...p, data]);
        dmLastTime.current = data.created_at;
        loadConversations();
      }
      setInput("");
      setAttachment(null);
    } catch (err) { setError(err?.response?.data?.detail || "Nachricht konnte nicht gesendet werden."); }
    finally { setSending(false); }
  }, [input, attachment, active, loadConversations]);

  /* ------------ Sidebar data ------------ */
  const filteredConvs = conversations.filter((c) => !search || c.other_name.toLowerCase().includes(search.toLowerCase()));
  const groupActive = active.type === "group";

  return (
    <div className="h-screen flex flex-col bg-black text-white antialiased overflow-hidden">
      <Navbar />
      <div className="flex flex-1 overflow-hidden max-w-7xl mx-auto w-full border-x border-white/[0.04]">
        {/* ============ Sidebar ============ */}
        <aside className={`w-full md:w-80 border-r border-white/[0.06] bg-black/40 shrink-0 flex flex-col ${showSidebarOnMobile ? "flex" : "hidden md:flex"}`} data-testid="chat-sidebar">
          <div className="px-4 py-4 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <h1 className="text-[18px] font-outfit font-bold tracking-tight text-white mb-0">Chats</h1>
              <span data-testid="ws-status"
                className={`text-[10px] font-outfit uppercase tracking-widest ${wsConnected ? "text-[#00e5ff]" : "text-white/30"}`}>
                <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1.5 ${wsConnected ? "bg-[#00e5ff] animate-pulse" : "bg-white/30"}`} />
                {wsConnected ? "Live" : "Offline"}
              </span>
            </div>
            <div className="relative mt-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Suchen..." data-testid="chat-search"
                className="pl-9 bg-white/[0.03] border-white/10 text-white rounded-full h-10 text-[13px] focus:border-[#00e5ff]/60 focus:ring-0" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            <div className="px-2 pt-3 pb-1 text-[9px] uppercase tracking-[0.28em] text-[#00e5ff]/70 font-outfit font-semibold">Gruppen-Chat</div>
            <button
              onClick={() => { setActive({ type: "group", channel: "allgemein" }); setShowSidebarOnMobile(false); }}
              data-testid="sidebar-group-chat"
              className={`relative w-full text-left flex items-center gap-3 px-3 py-3 transition-colors ${groupActive ? "bg-[#00e5ff]/[0.08]" : "hover:bg-white/[0.03]"}`}>
              {groupActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-9 bg-[#00e5ff]" />}
              <div className={`w-11 h-11 shrink-0 flex items-center justify-center border-2 ${groupActive ? "border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff]" : "border-white/15 bg-white/[0.03] text-white/70"}`}>
                <Users size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[14px] font-outfit font-semibold tracking-tight ${groupActive ? "text-[#00e5ff]" : "text-white"}`}>Gruppen-Chat</span>
                </div>
                <p className="text-[12px] text-white/45 truncate">Öffentliche Community</p>
              </div>
            </button>

            {filteredConvs.length > 0 && (
              <div className="px-2 pt-4 pb-1 text-[9px] uppercase tracking-[0.28em] text-white/40 font-outfit font-semibold">Direkt-Nachrichten</div>
            )}
            {filteredConvs.map((c) => {
              const a = active.type === "dm" && active.peer.id === c.other_id;
              return (
                <button key={c.other_id} onClick={() => { setActive({ type: "dm", peer: { id: c.other_id, name: c.other_name, company: c.other_company, avatar_url: c.other_avatar_url } }); setShowSidebarOnMobile(false); }}
                  data-testid={`dm-conv-${c.other_id}`}
                  className={`relative w-full text-left flex items-center gap-3 px-3 py-2.5 transition-colors ${a ? "bg-[#00e5ff]/[0.08]" : "hover:bg-white/[0.03]"}`}>
                  {a && <span className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-9 bg-[#00e5ff]" />}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPeerPreview({
                        avatar_url: c.other_avatar_url || "",
                        name: c.other_name || "",
                        subline: c.other_company || "",
                        id: c.other_id,
                      });
                    }}
                    data-testid={`dm-conv-avatar-${c.other_id}`}
                    className="shrink-0 cursor-pointer"
                    title="Profilbild ansehen"
                  >
                    <Avatar src={c.other_avatar_url} name={c.other_name} size={44} className={a ? "ring-2 ring-[#00e5ff]/50" : ""} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`text-[14px] font-outfit font-semibold truncate ${a ? "text-[#00e5ff]" : "text-white"}`}>{c.other_name || "Unbekannt"}</span>
                      {c.unread > 0 && <span className="shrink-0 text-[10px] bg-[#00e5ff] text-black px-1.5 py-0.5 font-outfit font-bold rounded-full">{c.unread}</span>}
                    </div>
                    <p className="text-[12px] text-white/45 truncate">{c.last_message}</p>
                  </div>
                </button>
              );
            })}

            {filteredConvs.length === 0 && !search && (
              <div className="px-4 py-8 text-center">
                <p className="text-[13px] text-white/35">Noch keine Direkt-Nachrichten.</p>
                <p className="text-[11px] text-white/25 mt-1 leading-relaxed">Starte ein Privatgespräch über die<br/><Link to="/members" className="text-[#00e5ff]/70 hover:text-[#00e5ff]">Mitglieder-Liste</Link>.</p>
              </div>
            )}
          </div>
        </aside>

        {/* ============ Main chat column ============ */}
        <main className={`flex-1 flex-col min-w-0 ${showSidebarOnMobile ? "hidden md:flex" : "flex"}`}>
          <div className="px-4 sm:px-6 py-3.5 border-b border-white/[0.06] bg-black/60 backdrop-blur-sm flex items-center gap-3">
            <button onClick={() => setShowSidebarOnMobile(true)} className="md:hidden text-white/60 hover:text-[#00e5ff] transition-colors" data-testid="chat-back-btn">
              <ArrowLeft size={18} />
            </button>
            {groupActive ? (
              <>
                <div className="w-10 h-10 shrink-0 border-2 border-[#00e5ff] bg-[#00e5ff]/10 text-[#00e5ff] flex items-center justify-center">
                  <Users size={17} />
                </div>
                <div>
                  <h2 className="text-[15px] font-outfit font-bold text-white tracking-tight" data-testid="chat-heading">Gruppen-Chat</h2>
                  <p className="text-[11px] text-white/45 font-outfit">Sichtbar für alle Mitglieder</p>
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setPeerPreview({
                    avatar_url: active.peer?.avatar_url || "",
                    name: active.peer?.name || "",
                    subline: active.peer?.company || "",
                    id: active.peer?.id,
                  })}
                  data-testid="chat-header-avatar"
                  className="group shrink-0"
                  title="Profilbild vergrößern"
                >
                  <Avatar src={active.peer?.avatar_url} name={active.peer?.name || "?"} size={40} className="group-hover:ring-2 group-hover:ring-[#00e5ff]/50 transition-all cursor-pointer" />
                </button>
                <div className="min-w-0">
                  <h2 className="text-[15px] font-outfit font-bold text-white tracking-tight truncate" data-testid="dm-heading">{active.peer?.name || "…"}</h2>
                  <p className="text-[11px] text-white/45 font-outfit">{active.peer?.company ? `${active.peer.company} · ` : ""}Privatgespräch · Ende-zu-Ende zwischen euch</p>
                </div>
              </>
            )}
          </div>

          {error && <div className="px-4 py-2 text-sm text-red-400 bg-red-400/10 border-b border-red-400/20">{error}</div>}

          <MessageList
            messages={groupActive ? groupMessages : dmMessages}
            me={me}
            messagesEndRef={messagesEndRef}
            emptyText={groupActive ? "Noch keine Nachrichten im Kanal. Schreib die erste!" : `Schreib ${active.peer?.name?.split(" ")[0] || "hier"} eine Nachricht.`}
            onAvatarClick={setPeerPreview}
          />

          <ChatInput
            value={input} onChange={setInput} onSend={handleSend} sending={sending}
            placeholder={groupActive ? "Nachricht an Gruppen-Chat... (STRG-V für Bilder)" : `Nachricht an ${active.peer?.name?.split(" ")[0] || ""}...`}
            testId={groupActive ? "chat-input" : "dm-input"}
            sendTestId={groupActive ? "chat-send-btn" : "dm-send-btn"}
            attachment={attachment} setAttachment={setAttachment}
          />
        </main>
      </div>

      {/* Avatar-Lightbox für Chat-Partner */}
      {peerPreview && (
        <AvatarLightbox
          src={peerPreview.avatar_url}
          name={peerPreview.name}
          subline={peerPreview.subline}
          onClose={() => setPeerPreview(null)}
          chatHref={null /* sind ja schon im Chat */}
        />
      )}
    </div>
  );
}
